import { FileText } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { DownloadPdfButton } from "@/components/ui/download-pdf-button";
import { ApiError, api } from "@/lib/api";
import type { DownloadBlock } from "@/types/blocks";

/** A generated report the user can download. The button POSTs the opaque
 *  `doc_ref` to the report-download endpoint, which re-checks tenant ownership
 *  and streams the archived PDF — never a markdown link. */
export function DownloadBlockView({ block }: { block: DownloadBlock }) {
  const download = useMutation({
    mutationFn: () =>
      api.download("/api/maxwell/reports/download", {
        doc_ref: block.doc_ref,
        filename: block.filename,
      }),
  });

  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
      <FileText size={20} className="shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="truncate text-sm font-medium text-foreground">{block.title}</p>
        <p className="truncate text-xs text-muted-foreground">
          {block.filename}
          {block.size_label ? ` · ${block.size_label}` : ""}
        </p>
      </div>
      <div className="ml-auto shrink-0">
        <DownloadPdfButton
          onClick={() =>
            download.mutate(undefined, {
              onError: (err) =>
                toast.error(
                  err instanceof ApiError ? err.message : "Failed to download the report.",
                ),
            })
          }
          pending={download.isPending}
        />
      </div>
    </div>
  );
}
